import React, { useEffect, useRef, useState } from 'react';
import { Lock } from 'lucide-react';
import useAppStore from '../../store/appStore';
import useAdminStore from '../../store/adminStore';
import { auth } from '../../config/firebase';
import {
  hashPin,
  isNeverLockPage,
  isPinSessionUnlocked,
  normalizePinLockConfig,
  PIN_SESSION_EVENT,
  setPinSessionUnlocked,
} from '../../utils/pinLockSession';

interface PagePinGateProps {
  currentPage: string;
  children: React.ReactNode;
}

const PagePinGate: React.FC<PagePinGateProps> = ({ currentPage, children }) => {
  const clientData = useAppStore((s) => s.clientData);
  const impersonatedClientId = useAdminStore((s) => s.impersonatedClientId);

  const isGodMode =
    !!impersonatedClientId ||
    !!sessionStorage.getItem('GOD_MODE_ID') ||
    !!(clientData as any)?._isGodMode;

  const mandantUid =
    impersonatedClientId ||
    auth.currentUser?.uid ||
    '';

  const pinLockConfig = normalizePinLockConfig(
    (clientData as any)?.pinLockConfig ?? null
  );

  const [sessionTick, setSessionTick] = useState(0);
  const [digits, setDigits] = useState(['', '', '', '']);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    const onChange = () => setSessionTick((t) => t + 1);
    window.addEventListener(PIN_SESSION_EVENT, onChange);
    return () => window.removeEventListener(PIN_SESSION_EVENT, onChange);
  }, []);

  useEffect(() => {
    setDigits(['', '', '', '']);
    setError(false);
  }, [currentPage, sessionTick]);

  const sessionUnlocked = mandantUid ? isPinSessionUnlocked(mandantUid) : false;
  void sessionTick;

  const needsLock =
    !isGodMode &&
    !!mandantUid &&
    !isNeverLockPage(currentPage) &&
    pinLockConfig.isPinActive &&
    !!pinLockConfig.pinHash &&
    pinLockConfig.lockedPageIds.includes(currentPage) &&
    !sessionUnlocked;

  useEffect(() => {
    if (!needsLock) return;
    const t = window.setTimeout(() => inputsRef.current[0]?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [needsLock, currentPage]);

  if (!needsLock) {
    return <>{children}</>;
  }

  const tryUnlock = async (pin: string) => {
    if (busy || pin.length !== 4 || !mandantUid) return;
    setBusy(true);
    try {
      const hashed = await hashPin(pin, mandantUid);
      if (hashed === pinLockConfig.pinHash) {
        setPinSessionUnlocked(mandantUid);
        setError(false);
        setDigits(['', '', '', '']);
        setSessionTick((t) => t + 1);
      } else {
        setError(true);
        setDigits(['', '', '', '']);
        window.setTimeout(() => {
          setError(false);
          inputsRef.current[0]?.focus();
        }, 450);
      }
    } finally {
      setBusy(false);
    }
  };

  const onDigit = (index: number, raw: string) => {
    const d = raw.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = d;
    setDigits(next);
    if (d && index < 3) {
      inputsRef.current[index + 1]?.focus();
    }
    if (d && index === 3 && next.every((x) => x.length === 1)) {
      void tryUnlock(next.join(''));
    }
  };

  const onKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const onPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (!pasted) return;
    const next = ['', '', '', ''];
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    if (pasted.length === 4) void tryUnlock(pasted);
    else inputsRef.current[pasted.length]?.focus();
  };

  return (
    <div className="w-full min-h-[60vh] flex items-center justify-center p-6">
      <div
        className={`w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl text-center ${
          error ? 'animate-[shake_0.4s_ease-in-out]' : ''
        }`}
        style={
          error
            ? ({
                animation: 'pe-pin-shake 0.4s ease-in-out',
              } as React.CSSProperties)
            : undefined
        }
      >
        <style>{`
          @keyframes pe-pin-shake {
            0%, 100% { transform: translateX(0); }
            20% { transform: translateX(-8px); }
            40% { transform: translateX(8px); }
            60% { transform: translateX(-6px); }
            80% { transform: translateX(6px); }
          }
        `}</style>
        <div className="mx-auto mb-5 w-14 h-14 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center">
          <Lock className="w-7 h-7 text-amber-400/90" strokeWidth={2} />
        </div>
        <h2 className="text-lg font-bold text-white tracking-wide mb-2 font-sans">
          Seite geschützt
        </h2>
        <p className="text-sm text-slate-400 mb-8 font-sans leading-relaxed">
          Diese Seite ist durch den Master-PIN geschützt.
        </p>
        <div className="flex justify-center gap-3 mb-4" onPaste={onPaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => {
                inputsRef.current[i] = el;
              }}
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              value={d}
              disabled={busy}
              onChange={(e) => onDigit(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
              className={`w-12 h-14 text-center text-xl font-mono rounded-lg bg-black border ${
                error ? 'border-red-500/70' : 'border-slate-700'
              } text-white focus:border-[#E6C299] focus:ring-1 focus:ring-[#E6C299] outline-none transition-colors`}
            />
          ))}
        </div>
        {error && (
          <p className="text-xs text-red-400 font-bold tracking-wide">PIN ungültig</p>
        )}
      </div>
    </div>
  );
};

export default PagePinGate;
